/**
 * useOperatingMode — J6
 *
 * Tiny wrapper over useRorosTenantSettings that surfaces the current mode plus
 * boolean helpers used to gate Growth-only navigation entries and pages.
 *
 * Usage:
 *   const { mode, isFounder, isGrowth, isScaling, isLoading } = useOperatingMode();
 *   if (isLoading || isFounder) return null; // hide Initiatives/Scorecard
 */

import { useRorosTenantSettings } from '@/hooks/roros/launchpad';

export type OperatingMode = 'founder' | 'growth' | 'scaling';

export interface UseOperatingModeResult {
  mode: OperatingMode | null;
  isFounder: boolean;
  isGrowth: boolean;
  isScaling: boolean;
  /** True when the mode is `growth` OR `scaling` (used to gate Growth surfaces). */
  isAtLeastGrowth: boolean;
  isLoading: boolean;
  isError: boolean;
}

export function useOperatingMode(): UseOperatingModeResult {
  const settingsQuery = useRorosTenantSettings();
  const settings = settingsQuery.data as { operating_mode?: string | null } | undefined;
  const raw = settings?.operating_mode ?? null;
  const mode: OperatingMode | null =
    raw === 'founder' || raw === 'growth' || raw === 'scaling' ? raw : null;

  return {
    mode,
    isFounder: mode === 'founder',
    isGrowth: mode === 'growth',
    isScaling: mode === 'scaling',
    isAtLeastGrowth: mode === 'growth' || mode === 'scaling',
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
  };
}
